<template>
  <HrPageLayout
    title="HR Events"
    subtitle="Manage company events"
    icon="📌"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Events' }]"
  >
    <template #header-actions>
      <button @click="createEvent" class="hr-btn hr-btn-primary">
        <i class="mdi mdi-plus"></i> Create Event
      </button>
      <button @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="mdi mdi-refresh"></i> Refresh
      </button>
      <button @click="exportEvents" class="hr-btn hr-btn-success">
        <i class="mdi mdi-file-export"></i> Export
      </button>
    </template>

    <div class="hr-events-content">
      <!-- Filters section -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">
            <i class="bx bx-filter-alt"></i> Event Filters
          </h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-filters-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Event Type</label>
              <select v-model="filters.eventType" class="hr-select">
                <option value="">All Event Types</option>
                <option v-for="type in eventTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="filters.departmentId" class="hr-select">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
            <div class="hr-filter-group" style="grid-column: span 2;">
              <label class="hr-form-label">Date Range</label>
              <div class="hr-date-range">
                <input v-model="filters.startDate" type="date" class="hr-input">
                <span class="range-sep">to</span>
                <input v-model="filters.endDate" type="date" class="hr-input">
              </div>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Search</label>
              <div class="hr-search-wrap">
                <input type="text" v-model="filters.search" class="hr-input" placeholder="Search by title, location...">
                <i class="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics metrics -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-calendar-check"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Events</p>
          <h2 class="hr-metric-value">{{ metrics.totalEvents || 0 }}</h2>
            </div>
          <span class="hr-metric-subtext">Past 30 days</span>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-calendar-star"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Upcoming Events</p>
          <h2 class="hr-metric-value">{{ metrics.upcomingEvents || 0 }}</h2>
            </div>
          <span class="hr-metric-subtext">Next 7 days</span>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-calendar-heart"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">This Month</p>
          <h2 class="hr-metric-value">{{ metrics.eventsThisMonth || 0 }}</h2>
            </div>
          <span class="hr-metric-subtext">{{ currentMonthName }}</span>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-calendar"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Today's Events</p>
          <h2 class="hr-metric-value">{{ metrics.todayEvents || 0 }}</h2>
            </div>
          <span class="hr-metric-subtext">{{ today }}</span>
        </div>
      </div>

      <!-- Events by Type & Upcoming Events -->
      <div class="hr-content-grid hr-mb-4">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-pie-chart-alt"></i> Events by Type</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0" class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th>Event Type</th>
                    <th style="text-align: center;">Count</th>
                    <th style="text-align: center;">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(count, type) in metrics.typeCounts" :key="type">
                    <td>{{ type }}</td>
                    <td style="text-align: center;">{{ count }}</td>
                    <td style="text-align: center;">
                      <div class="hr-progress-bar">
                        <div class="hr-progress-fill" :class="getEventTypeColor(type)" :style="{ width: getEventTypePercentage(type) + '%' }"></div>
                      </div>
                      {{ getEventTypePercentage(type) }}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="hr-empty-state">
              <i class="bx bx-bar-chart-alt-2"></i>
              <p>No event type data available</p>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-calendar-event"></i> Upcoming Events</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="upcomingEvents.length > 0" class="upcoming-events-list">
              <div v-for="event in upcomingEvents" :key="event.id" class="upcoming-event-item hr-mb-3">
                <div class="hr-flex hr-flex-center hr-upcoming-event-row">
                  <div class="event-date-badge hr-me-3">
                    <span class="date-month">{{ formatDateMonth(event.startDate) }}</span>
                    <span class="date-day">{{ formatDateDay(event.startDate) }}</span>
                  </div>
                  <div class="event-details hr-flex-1">
                    <h6 class="event-title hr-mb-1">{{ event.title }}</h6>
                    <div class="event-meta">
                      <span><i class="bx bx-time hr-me-2"></i>{{ formatEventTime(event) }}</span>
                      <span class="hr-me-2"><i class="bx bx-map hr-me-2"></i>{{ event.location || 'N/A' }}</span>
                    </div>
                  </div>
                  <span class="hr-badge" :class="getEventTypeColor(event.eventType)">{{ event.eventType }}</span>
                </div>
              </div>
            </div>
            <div v-else class="hr-empty-state">
              <i class="bx bx-calendar"></i>
              <p>No upcoming events</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Events list table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Events List</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading events data...</p>
          </div>

          <div v-else-if="!events.length" class="hr-empty-state">
            <i class="bx bx-calendar-x"></i>
            <h5>No events found</h5>
            <p>Try adjusting your filters or create a new event</p>
            <button @click="resetFilters" class="hr-btn hr-btn-primary hr-btn-sm">Reset Filters</button>
          </div>

          <template v-else>
            <div class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Type</th>
                    <th>Date & Time</th>
                    <th>Location</th>
                    <th>Audience</th>
                    <th>Status</th>
                    <th style="text-align: center;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="event in events" :key="event.id">
                    <td>
                      <div class="hr-flex hr-flex-center hr-gap-2">
                        <div class="avatar-xs">
                          <i class="bx bx-calendar"></i>
                        </div>
                        <div>
                          <div class="event-row-title">{{ event.title }}</div>
                          <div class="event-row-code">{{ event.eventCode }}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getEventTypeColor(event.eventType)">{{ event.eventType }}</span>
                    </td>
                    <td>
                      <div>
                        <span class="d-block"><i class="bx bx-calendar hr-me-2"></i>{{ formatDate(event.startDate) }}</span>
                        <span v-if="event.startTime" class="event-time-text">
                          <i class="bx bx-time hr-me-2"></i>{{ formatTime(event.startTime) }} - {{ formatTime(event.endTime) }}
                        </span>
                      </div>
                    </td>
                    <td>{{ event.location || 'N/A' }}</td>
                    <td>{{ event.targetAudience || 'All Employees' }}</td>
                    <td>
                      <span class="hr-badge" :class="getStatusBadgeClass(event.status)">{{ event.status }}</span>
                    </td>
                    <td style="text-align: center;">
                      <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content: center;">
                        <button @click="viewEventDetails(event)" class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" title="View Details">
                          <i class="bx bx-show"></i>
                        </button>
                        <button @click="editEvent(event)" class="hr-btn hr-btn-primary hr-btn-sm hr-btn-icon" title="Edit Event">
                          <i class="bx bx-edit"></i>
                        </button>
                        <button v-if="event.status !== 'Canceled'" @click="cancelEvent(event)" class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon" title="Cancel Event">
                          <i class="bx bx-x"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="events.length" class="hr-pagination">
              <div class="hr-pagination-info">
                Showing <strong>{{ events.length }}</strong> of <strong>{{ pagination.totalEvents }}</strong> events
              </div>
              <nav v-if="pagination.totalPages > 1" class="hr-pagination-nav">
                <button
                  class="hr-page-btn"
                  :disabled="pagination.currentPage <= 1"
                  @click="changePage(pagination.currentPage - 1)"
                >
                  <i class="bx bx-chevron-left"></i>
                </button>
                <template v-for="page in getPageNumbers()" :key="page">
                  <button
                    v-if="page !== '...'"
                    class="hr-page-btn"
                    :class="{ active: page === pagination.currentPage }"
                    @click="changePage(page)"
                  >
                    {{ page }}
                  </button>
                  <span v-else class="hr-page-btn ellipsis">...</span>
                </template>
                <button
                  class="hr-page-btn"
                  :disabled="pagination.currentPage >= pagination.totalPages"
                  @click="changePage(pagination.currentPage + 1)"
                >
                  <i class="bx bx-chevron-right"></i>
                </button>
              </nav>
            </div>
          </template>
        </div>
      </div>

              <!-- Event Create/Edit Modal -->
              <ModalWrapper
                ref="createModal"
                :title="isEditMode ? 'Edit Event' : 'Create New Event'"
                size="large"
              >
                <template #body>
                  <form @submit.prevent="submitEvent" class="events-modal-form">
                    <div class="hr-form-grid">
                      <div class="span-full">
                        <label class="hr-form-label">Event Title <span class="required">*</span></label>
                        <input v-model="eventForm.title" type="text" class="hr-input" required>
                      </div>
                      <div>
                        <label class="hr-form-label">Event Type <span class="required">*</span></label>
                        <select v-model="eventForm.eventType" class="hr-select" required>
                          <option value="" disabled>Select Event Type</option>
                          <option v-for="type in eventTypes" :key="type" :value="type">{{ type }}</option>
                        </select>
                      </div>
                      <div class="span-full">
                        <label class="hr-form-label">Target Audience <span class="required">*</span></label>
                        <div style="display: flex; gap: 15px;">
                          <label class="hr-radio-wrap">
                            <input type="radio" v-model="eventForm.targetAudience" value="All Employees">
                            <span>All Employees</span>
                          </label>
                          <label class="hr-radio-wrap">
                            <input type="radio" v-model="eventForm.targetAudience" value="Specific Departments">
                            <span>Specific Departments</span>
                          </label>
                          <label class="hr-radio-wrap">
                            <input type="radio" v-model="eventForm.targetAudience" value="Specific Employees">
                            <span>Specific Employees</span>
                          </label>
                        </div>
                      </div>

                      <div v-if="eventForm.targetAudience === 'Specific Departments'" class="span-full">
                        <label class="hr-form-label">Select Departments <span class="required">*</span></label>
                        <div class="hr-checkbox-group-scroll" style="max-height: 150px; overflow-y: auto; border: 1px solid #ddd; padding: 10px; border-radius: 4px;">
                          <label v-for="dept in departments" :key="dept.id" class="hr-checkbox-wrap" style="display: block; margin-bottom: 5px;">
                            <input type="checkbox" v-model="eventForm.departments" :value="dept.id">
                            <span>{{ dept.departmentName }}</span>
                          </label>
                        </div>
                      </div>

                      <div v-if="eventForm.targetAudience === 'Specific Employees'" class="span-full">
                        <label class="hr-form-label">Select Employees <span class="required">*</span></label>
                        <select v-model="eventForm.employees" class="hr-select" multiple size="6">
                          <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                            {{ employee.firstName }} {{ employee.lastName }} ({{ employee.departmentName }})
                          </option>
                        </select>
                        <small class="hr-form-hint">Hold Ctrl/Cmd to select multiple employees</small>
                      </div>
                      <div>
                        <label class="hr-form-label">Start Date <span class="required">*</span></label>
                        <input v-model="eventForm.startDate" type="date" class="hr-input" required>
                      </div>
                      <div>
                        <label class="hr-form-label">End Date</label>
                        <input v-model="eventForm.endDate" type="date" class="hr-input">
                      </div>
                      <div>
                        <label class="hr-form-label">Start Time</label>
                        <input v-model="eventForm.startTime" type="time" class="hr-input">
                      </div>
                      <div>
                        <label class="hr-form-label">End Time</label>
                        <input v-model="eventForm.endTime" type="time" class="hr-input">
                      </div>
                      <div class="span-full">
                        <label class="hr-form-label">Location</label>
                        <input v-model="eventForm.location" type="text" class="hr-input">
                      </div>
                      <div>
                        <label class="hr-form-label">Virtual Platform</label>
                        <select v-model="eventForm.virtualMeetingPlatform" class="hr-select">
                          <option value="">None</option>
                          <option value="Google Meet">Google Meet</option>
                          <option value="Zoom">Zoom</option>
                          <option value="Microsoft Teams">Microsoft Teams</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label class="hr-form-label">Virtual Meeting Link</label>
                        <input 
                          v-if="!eventForm.virtualMeetingPlatform || eventForm.virtualMeetingPlatform === 'Other' || !isIntegrationConnected(eventForm.virtualMeetingPlatform)" 
                          v-model="eventForm.virtualMeetingLink" 
                          type="text" 
                          class="hr-input" 
                          :disabled="!eventForm.virtualMeetingPlatform"
                          placeholder="Enter meeting link"
                        >
                        <input 
                          v-else 
                          type="text" 
                          class="hr-input" 
                          disabled 
                          placeholder="Link will be auto-generated by StaffSync"
                        >
                        <small v-if="eventForm.virtualMeetingPlatform && eventForm.virtualMeetingPlatform !== 'Other' && !isIntegrationConnected(eventForm.virtualMeetingPlatform)" class="hr-text-warning hr-mt-1 hr-d-block">
                          <i class="bx bx-error-circle"></i> Account not connected. Link will not be auto-generated.
                        </small>
                      </div>
                      <div>
                        <label class="hr-form-label">Priority</label>
                        <select v-model="eventForm.priority" class="hr-select">
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                        </select>
                      </div>
                      <div>
                        <label class="hr-form-label">Visibility</label>
                        <select v-model="eventForm.visibility" class="hr-select">
                          <option value="Public">Public</option>
                          <option value="Department">Department Only</option>
                          <option value="Private">Private</option>
                        </select>
                      </div>
                      <div class="span-full">
                        <label class="hr-form-label">Event Description</label>
                        <textarea v-model="eventForm.description" class="hr-textarea" rows="3"></textarea>
                      </div>

                      <div class="span-full">
                        <label class="hr-form-label">Attachments</label>
                        <input type="file" class="hr-input" multiple>
                      </div>
                    </div>
                  </form>
                </template>
                <template #footer>
                  <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.createModal.close()">Close</button>
                  <button type="button" class="hr-btn hr-btn-primary" @click="submitEvent">
                    {{ isEditMode ? 'Update Event' : 'Create Event' }}
                  </button>
                </template>
              </ModalWrapper>

              <!-- Event Details Modal -->
              <ModalWrapper
                ref="detailsModal"
                :title="'Event Details' + (selectedEvent ? ': ' + selectedEvent.title : '')"
                size="large"
              >
                <template #body>
                  <div v-if="selectedEvent" class="event-details-body">
                    <div class="hr-flex hr-flex-between hr-mb-4 hr-flex-wrap hr-gap-3">
                      <div>
                        <h4 class="event-detail-title">{{ selectedEvent.title }}</h4>
                        <p class="event-detail-meta hr-mb-1">
                          <span class="hr-badge" :class="getEventTypeColor(selectedEvent.eventType)">{{ selectedEvent.eventType }}</span>
                        </p>
                        <p class="event-detail-code">Event Code: {{ selectedEvent.eventCode }}</p>
                      </div>
                      <span class="hr-badge" :class="getStatusBadgeClass(selectedEvent.status)">{{ selectedEvent.status }}</span>
                    </div>

                    <div class="hr-content-grid hr-mb-4">
                      <div class="hr-info-block">
                        <div class="hr-info-block-header">Event Information</div>
                        <div class="hr-info-block-body">
                          <div class="hr-info-row">
                            <span><i class="bx bx-calendar hr-me-2"></i>Date:</span>
                            <span>{{ formatDate(selectedEvent.startDate) }}</span>
                          </div>
                          <div v-if="selectedEvent.startTime" class="hr-info-row">
                            <span><i class="bx bx-time hr-me-2"></i>Time:</span>
                            <span>{{ formatTime(selectedEvent.startTime) }} - {{ formatTime(selectedEvent.endTime) }}</span>
                          </div>
                          <div class="hr-info-row">
                            <span><i class="bx bx-map hr-me-2"></i>Location:</span>
                            <span>{{ selectedEvent.location || 'N/A' }}</span>
                          </div>
                          <div v-if="selectedEvent.virtualMeetingLink" class="hr-info-row">
                            <span><i class="bx bx-video hr-me-2"></i>Virtual Link:</span>
                            <a :href="selectedEvent.virtualMeetingLink" target="_blank" class="hr-link">Join Meeting</a>
                          </div>
                          <div class="hr-info-row">
                            <span><i class="bx bx-buildings hr-me-2"></i>Department:</span>
                            <span>{{ selectedEvent.departmentName || 'All Departments' }}</span>
                          </div>
                          <div class="hr-info-row">
                            <span><i class="bx bx-show hr-me-2"></i>Visibility:</span>
                            <span>{{ selectedEvent.visibility }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="hr-info-block">
                        <div class="hr-info-block-header">Description</div>
                        <div class="hr-info-block-body">
                          <p v-if="selectedEvent.description">{{ selectedEvent.description }}</p>
                          <p v-else class="hr-text-muted hr-text-center">No description provided</p>
                        </div>
                      </div>
                    </div>

                    <div class="hr-info-block hr-mb-4">
                      <div class="hr-info-block-header">Attendees</div>
                      <div class="hr-info-block-body">
                        <div v-if="selectedEvent.attendeesList && selectedEvent.attendeesList.length > 0" class="hr-table-wrapper">
                          <table class="hr-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Department</th>
                                <th>Response</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(attendee, index) in selectedEvent.attendeesList" :key="index">
                                <td>{{ attendee.name }}</td>
                                <td>{{ attendee.department || 'N/A' }}</td>
                                <td>
                                  <span class="hr-badge" :class="getResponseBadgeClass(attendee.response)">{{ attendee.response || 'Pending' }}</span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p v-else class="hr-text-muted hr-text-center">No attendees added to this event</p>
                      </div>
                    </div>

                    <div v-if="selectedEvent.attachmentsList && selectedEvent.attachmentsList.length > 0" class="hr-info-block hr-mb-4">
                      <div class="hr-info-block-header">Attachments</div>
                      <div class="hr-info-block-body">
                        <div class="hr-attachments-grid">
                          <div v-for="(attachment, index) in selectedEvent.attachmentsList" :key="index" class="hr-attachment-card">
                            <div class="hr-flex">
                              <i class="bx bx-file file-icon"></i>
                              <div>
                                <div class="file-name">{{ attachment.originalName }}</div>
                                <div class="file-size">{{ formatFileSize(attachment.size) }}</div>
                              </div>
                            </div>
                            <div class="hr-mt-2" style="text-align: right;">
                              <a :href="attachment.path" target="_blank" class="hr-btn hr-btn-primary hr-btn-sm">
                                <i class="bx bx-download hr-me-2"></i>Download
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="hr-info-block">
                      <div class="hr-info-block-header">Event History</div>
                      <div class="hr-info-block-body">
                        <ul class="timeline">
                          <li class="timeline-item">
                            <div class="timeline-marker timeline-primary"></div>
                            <div class="timeline-content">
                              <h6 class="timeline-title">Event Created</h6>
                              <p class="timeline-text hr-mb-0">By: {{ selectedEvent.createdBy }}</p>
                              <p class="timeline-text timeline-muted">{{ formatDateTime(selectedEvent.createdAt) }}</p>
                            </div>
                          </li>
                          <li v-if="selectedEvent.updatedAt !== selectedEvent.createdAt" class="timeline-item">
                            <div class="timeline-marker timeline-info"></div>
                            <div class="timeline-content">
                              <h6 class="timeline-title">Event Updated</h6>
                              <p class="timeline-text hr-mb-0">By: {{ selectedEvent.updatedBy || selectedEvent.createdBy }}</p>
                              <p class="timeline-text timeline-muted">{{ formatDateTime(selectedEvent.updatedAt) }}</p>
                            </div>
                          </li>
                          <li v-if="selectedEvent.status === 'Canceled'" class="timeline-item">
                            <div class="timeline-marker timeline-danger"></div>
                            <div class="timeline-content">
                              <h6 class="timeline-title">Event Canceled</h6>
                              <p class="timeline-text hr-mb-0">By: {{ selectedEvent.canceledBy }}</p>
                              <p class="timeline-text timeline-muted">{{ formatDateTime(selectedEvent.canceledAt) }}</p>
                              <p v-if="selectedEvent.cancellationReason" class="timeline-text">Reason: {{ selectedEvent.cancellationReason }}</p>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </template>
                <template #footer>
                  <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.detailsModal.close()">Close</button>
                  <button type="button" class="hr-btn hr-btn-primary" @click="editEvent(selectedEvent)">Edit Event</button>
                  <button
                      v-if="selectedEvent && selectedEvent.status !== 'Canceled'"
                      type="button"
                      class="hr-btn hr-btn-danger"
                      @click="cancelEvent(selectedEvent)"
                  >
                    Cancel Event
                  </button>
                </template>
              </ModalWrapper>

              <!-- Cancel Event Modal -->
              <ModalWrapper
                ref="cancelModal"
                title="Cancel Event"
              >
                <template #body>
                  <div v-if="selectedEvent">
                    <p>Are you sure you want to cancel the event: <strong>{{ selectedEvent.title }}</strong>?</p>
                    <div class="hr-mb-3">
                      <label class="hr-form-label">Reason for Cancellation</label>
                      <textarea v-model="cancellationReason" class="hr-textarea" rows="3" placeholder="Enter reason for cancellation"></textarea>
                    </div>
                    <label class="hr-checkbox-wrap">
                      <input type="checkbox" v-model="notifyAttendees" id="notifyAttendees">
                      <span>Notify all attendees about cancellation</span>
                    </label>
                  </div>
                </template>
                <template #footer>
                  <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.cancelModal.close()">Close</button>
                  <button type="button" class="hr-btn hr-btn-danger" @click="confirmCancelEvent">Confirm Cancellation</button>
                </template>
              </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "HREvents",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      events: [],
      upcomingEvents: [],
      departments: [],
      employees: [],
      eventTypes: ['Meeting', 'Training', 'Holiday', 'Deadline', 'Conference', 'Team Building', 'Announcement', 'Other'],
      filters: {
        departmentId: "",
        eventType: "",
        startDate: this.getDateOffset(-30), // Last 30 days
        endDate: this.getDateOffset(30), // Next 30 days
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalEvents: 0,
        limit: 10
      },
      metrics: {
        totalEvents: 0,
        upcomingEvents: 0,
        eventsThisMonth: 0,
        todayEvents: 0,
        typeCounts: {}
      },
      selectedEvent: null,
      eventForm: {
        title: "",
        description: "",
        eventType: "",
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        location: "",
        virtualMeetingLink: "",
        departmentId: "",
        attendees: [],
        isAllDay: false,
        priority: "Medium",
        visibility: "Public"
      },
      isEditMode: false,
      cancellationReason: "",
      notifyAttendees: true,
      integrations: {}
    };
  },
  computed: {
    today() {
      return new Date().toLocaleDateString();
    },
    currentMonthName() {
      return new Date().toLocaleString('default', { month: 'long' });
    }
  },
  mounted() {
    this.getDepartments();
    this.getEmployees();
    this.getEvents();
    this.loadIntegrations();
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
              this.showToast("error", "Failed to load employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employees");
          });
    },

    getEvents() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getEvents");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.eventType) {
        data.append("eventType", this.filters.eventType);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.events = res.data.data.events || [];
              this.metrics = res.data.data.metrics || {};
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalEvents: 0,
                limit: 10
              };

              // Get upcoming events (next 7 days)
              const today = new Date();
              const nextWeek = new Date();
              nextWeek.setDate(today.getDate() + 7);

              this.upcomingEvents = this.events
                  .filter(event => {
                    const eventDate = new Date(event.startDate);
                    return eventDate >= today && eventDate <= nextWeek && event.status !== 'Canceled';
                  })
                  .slice(0, 5); // Top 5 upcoming events
            } else {
              console.error("Error fetching events:", res.data.message);
              this.showToast("error", "Failed to load events data: " + res.data.message);
              this.events = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching events");
            this.events = [];
            this.metrics = {};
          });
    },

    getEventDetails(eventId) {
      const data = new FormData();
      data.append("function", "getEventDetails");
      data.append("eventId", eventId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedEvent = res.data.data.event;
              // Open the event details modal
              this.$nextTick(() => this.$refs.detailsModal?.open());
            } else {
              console.error("Error fetching event details:", res.data.message);
              this.showToast("error", "Failed to load event details: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching event details");
          });
    },

    refreshData() {
      this.getEvents();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        eventType: "",
        startDate: this.getDateOffset(-30),
        endDate: this.getDateOffset(30),
        search: "",
        page: 1,
        limit: 10
      };
      this.getEvents();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getEvents();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    createEvent() {
      this.isEditMode = false;
      this.eventForm = {
        title: "",
        description: "",
        eventType: "",
        startDate: new Date().toISOString().split('T')[0], // Today's date
        endDate: "",
        startTime: "",
        endTime: "",
        location: "",
        virtualMeetingPlatform: "",
        virtualMeetingLink: "",
        targetAudience: "All Employees",
        departments: [],
        employees: [],
        isAllDay: false,
        priority: "Medium",
        visibility: "Public"
      };
      this.$nextTick(() => this.$refs.createModal?.open());
    },

    editEvent(event) {
      this.isEditMode = true;
      this.selectedEvent = event;

      this.$refs.detailsModal?.close();

      // Prepare form data
      this.eventForm = {
        id: event.id,
        title: event.title,
        description: event.description || "",
        eventType: event.eventType,
        startDate: event.startDate,
        endDate: event.endDate || "",
        startTime: event.startTime || "",
        endTime: event.endTime || "",
        location: event.location || "",
        virtualMeetingPlatform: event.virtualMeetingPlatform || "",
        virtualMeetingLink: event.virtualMeetingLink || "",
        targetAudience: event.targetAudience || "All Employees",
        departments: event.departmentsList ? event.departmentsList.map(dept => dept.id) : [],
        employees: event.employeesList ? event.employeesList.map(emp => emp.id) : [],
        isAllDay: event.isAllDay === 1,
        priority: event.priority || "Medium",
        visibility: event.visibility || "Public"
      };

      this.$nextTick(() => this.$refs.createModal?.open());
    },

    async loadIntegrations() {
      try {
        const data = new FormData();
        data.append("function", "getUserIntegrations");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.integrations = res.data.data || {};
        }
      } catch (error) {
        console.error("Failed to load integrations:", error);
      }
    },

    isIntegrationConnected(platform) {
      if (!platform || platform === 'Other') return false;
      return !!this.integrations[platform]?.connected;
    },

    submitEvent() {
      // Validate required fields
      if (!this.eventForm.title || !this.eventForm.startDate || !this.eventForm.eventType) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      // Prepare form data
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateEvent");
        data.append("eventId", this.eventForm.id);
      } else {
        data.append("function", "createEvent");
      }

      // Add all form fields
      data.append("title", this.eventForm.title);
      data.append("description", this.eventForm.description);
      data.append("eventType", this.eventForm.eventType);
      data.append("startDate", this.eventForm.startDate);
      data.append("endDate", this.eventForm.endDate);
      data.append("startTime", this.eventForm.startTime);
      data.append("endTime", this.eventForm.endTime);
      data.append("location", this.eventForm.location);
      data.append("virtualMeetingPlatform", this.eventForm.virtualMeetingPlatform);
      data.append("virtualMeetingLink", this.eventForm.virtualMeetingLink);
      data.append("targetAudience", this.eventForm.targetAudience);
      data.append("isAllDay", this.eventForm.isAllDay ? 1 : 0);
      data.append("priority", this.eventForm.priority);
      data.append("visibility", this.eventForm.visibility);

      // Handle departments
      if (this.eventForm.targetAudience === 'Specific Departments' && this.eventForm.departments.length > 0) {
        data.append("departments", JSON.stringify(this.eventForm.departments));
      } else {
        data.append("departments", "[]");
      }

      // Handle employees
      if (this.eventForm.targetAudience === 'Specific Employees' && this.eventForm.employees.length > 0) {
        data.append("employees", JSON.stringify(this.eventForm.employees));
      } else {
        data.append("employees", "[]");
      }

      // Send the request
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", this.isEditMode ? "Event updated successfully" : "Event created successfully");

              this.$refs.createModal?.close();

              // Refresh the events data
              this.getEvents();
            } else {
              console.error("Error saving event:", res.data.message);
              this.showToast("error", `Failed to ${this.isEditMode ? 'update' : 'create'} event: ${res.data.message}`);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", `Network error when ${this.isEditMode ? 'updating' : 'creating'} event`);
          });
    },

    viewEventDetails(event) {
      this.selectedEvent = null;
      this.getEventDetails(event.id);
    },

    cancelEvent(event) {
      this.selectedEvent = event;
      this.cancellationReason = "";
      this.notifyAttendees = true;

      this.$refs.detailsModal?.close();
      this.$nextTick(() => this.$refs.cancelModal?.open());
    },

    confirmCancelEvent() {
      if (!this.selectedEvent) return;

      const data = new FormData();
      data.append("function", "cancelEvent");
      data.append("eventId", this.selectedEvent.id);
      data.append("reason", this.cancellationReason);
      data.append("notifyAttendees", this.notifyAttendees ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Event canceled successfully");

              this.$refs.cancelModal?.close();

              // Refresh the events data
              this.getEvents();
            } else {
              console.error("Error canceling event:", res.data.message);
              this.showToast("error", "Failed to cancel event: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when canceling event");
          });
    },

    exportEvents() {
      const data = new FormData();
      data.append("function", "exportEventsReport");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.eventType) {
        data.append("eventType", this.filters.eventType);
      }

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    // Helper methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatTime(timeString) {
      if (!timeString) return '';

      // If the time string is already in 12-hour format
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }

      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = timeString.split(':');
      let hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12;
      hour = hour ? hour : 12; // the hour '0' should be '12'
      return `${hour}:${minutes} ${ampm}`;
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    formatEventTime(event) {
      if (event.isAllDay) return 'All Day';
      if (!event.startTime) return 'No Time Specified';

      return `${this.formatTime(event.startTime)}${event.endTime ? ' - ' + this.formatTime(event.endTime) : ''}`;
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getEventTypeColor(type) {
      if (!type) return 'hr-badge-muted';
      const typeMap = {
        'Meeting': 'hr-badge-success',
        'Training': 'hr-badge-purple',
        'Holiday': 'hr-badge-warning',
        'Deadline': 'hr-badge-danger',
        'Conference': 'hr-badge-info',
        'Team Building': 'hr-badge-success',
        'Announcement': 'hr-badge-warning',
        'Other': 'hr-badge-muted'
      };
      return typeMap[type] || 'hr-badge-muted';
    },

    getStatusBadgeClass(status) {
      if (!status) return 'hr-badge-muted';
      const statusMap = {
        'Scheduled': 'hr-badge-success',
        'Canceled': 'hr-badge-danger',
        'Completed': 'hr-badge-info',
        'In Progress': 'hr-badge-warning',
        'Postponed': 'hr-badge-warning'
      };
      return statusMap[status] || 'hr-badge-muted';
    },

    getResponseBadgeClass(response) {
      if (!response) return 'hr-badge-muted';
      const responseMap = {
        'Accepted': 'hr-badge-success',
        'Declined': 'hr-badge-danger',
        'Tentative': 'hr-badge-warning',
        'Pending': 'hr-badge-muted'
      };
      return responseMap[response] || 'hr-badge-muted';
    },

    getEventTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalEvents) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      return ((count / this.metrics.totalEvents) * 100).toFixed(1);
    },

    showToast(type, message) {
      // Check if toast plugin exists before using it
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback to alert if toast plugin not available
        alert(message);
      }
    }
  },
  watch: {
    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 300),

    'filters.eventType': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 300),

    'filters.startDate': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 300),

    'filters.endDate': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getEvents();
    }, 500)
  }
};
</script>


<style scoped>
.avatar-xs {
  height: 2rem;
  width: 2rem;
  min-width: 2rem;
  background: #f1f5f9;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4f46e5;
}

.avatar-xs i {
  font-size: 18px;
}

.event-date-badge {
  min-width: 56px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  text-align: center;
}

.date-month {
  text-transform: uppercase;
  font-size: 11px;
  color: #64748b;
  display: block;
}

.date-day {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  display: block;
}

.hr-upcoming-event-row {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.event-details {
  min-width: 0;
}

.event-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.event-meta {
  font-size: 13px;
  color: #64748b;
}

.event-detail-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.event-detail-meta {
  margin: 0 0 4px 0;
}

.event-detail-code {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.event-row-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.event-row-code {
  font-size: 12px;
  color: #64748b;
}

.event-time-text {
  font-size: 12px;
  color: #64748b;
}

.d-block { display: block; }

.timeline {
  list-style: none;
  padding-left: 30px;
  margin: 0;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-marker {
  position: absolute;
  left: -30px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  top: 4px;
}

.timeline-marker.timeline-primary { background: #3b82f6; }
.timeline-marker.timeline-info { background: #06b6d4; }
.timeline-marker.timeline-danger { background: #ef4444; }

.timeline-content {
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.timeline-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}

.timeline-text {
  font-size: 13px;
  line-height: 1.5;
}

.timeline-muted {
  color: #94a3b8;
}
</style>